import type { FirebaseError } from "firebase/app";
import type {
  AuthCredential,
  Auth as FirebaseAuth,
  IdTokenResult,
  User,
  UserCredential,
  UserInfo,
} from "firebase/auth";
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  OAuthProvider,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { setUserIdForAnalytics, setUserRoleForAnalytics } from "../misc/firebase";
import { mockAuth } from "./auth.mock";

export type UserRole = "ADMIN" | "CUSTOMER" | "AGENT" | "PROVIDER";

interface AgentCustomPrivileges {
  readonly onlyOwnCustomersOrders: boolean;
  readonly onlyPublicAttachments: boolean;
  readonly onlyOwnCustomersEdit: boolean;
  readonly cannotChangeOptionPrices: boolean;
  readonly cannotExportOrders: boolean;
  readonly canViewProvidersAndProducts: boolean;
  readonly canChangeCustomerAccountManager: boolean;
  readonly canEditProvidersProductsAndDictionaries: boolean;
  readonly canEditPriceLists: boolean;
  readonly canEditPackages: boolean;
  readonly canDeleteCustomerNotes: boolean;
  readonly cannotViewCustomers: boolean;
  readonly canViewStatistics: boolean;
}

interface CustomerCustomPrivileges {
  readonly canOrderCalculation: boolean;
  readonly canCreateCalculationFromProducts: boolean;
}

export interface AdminContext {
  readonly role: "ADMIN";
  readonly agentId?: string;
}

export interface CustomerContext {
  readonly role: "CUSTOMER";
  readonly customerId: string;
  readonly customPrivileges: CustomerCustomPrivileges;
}

export interface AgentContext {
  readonly role: "AGENT";
  readonly agentId: string;
  readonly customPrivileges: AgentCustomPrivileges;
}

export interface ProviderContext {
  readonly role: "PROVIDER";
  readonly providerId: string;
}

export type UserContext = AdminContext | CustomerContext | AgentContext | ProviderContext;

export function getAgentIdIfPresent(userContext: UserContext | undefined | null): string | undefined {
  if (userContext?.role === "AGENT" || userContext?.role === "ADMIN") {
    return userContext.agentId;
  }
  return undefined;
}

export function isAgentWithProductsAndProviderAccess(userContext: UserContext | undefined | null): boolean {
  return userContext?.role === "AGENT" && userContext.customPrivileges.canViewProvidersAndProducts;
}

export function isAgentWithProvidersProductsAndDictionariesAccess(
  userContext: UserContext | undefined | null,
): boolean {
  return userContext?.role === "AGENT" && userContext.customPrivileges.canEditProvidersProductsAndDictionaries;
}

export function isAgentWithPackagesAccess(userContext: UserContext | undefined | null): boolean {
  return userContext?.role === "AGENT" && userContext.customPrivileges.canEditPackages;
}

export function canAccessCustomers(userContext: UserContext | undefined | null): boolean {
  return (
    userContext?.role === "ADMIN" ||
    (userContext?.role === "AGENT" && !userContext.customPrivileges.cannotViewCustomers)
  );
}

export function canAccessStatistics(userContext: UserContext | undefined | null): boolean {
  return (
    userContext?.role === "ADMIN" || (userContext?.role === "AGENT" && userContext.customPrivileges.canViewStatistics)
  );
}

export type AuthCallback = (user: { info: UserInfo; context: UserContext } | null) => void;

export function contextFromToken(token: IdTokenResult): UserContext {
  const roles = (token.claims.roles as string[] | undefined) ?? [];
  if (roles.includes("ADMIN")) {
    const agentId = token.claims.agentId as string | undefined;
    return { role: "ADMIN", agentId };
  }
  if (roles.includes("AGENT")) {
    const agentId = token.claims.agentId as string;
    const customPrivileges = (token.claims.customPrivileges ?? {}) as AgentCustomPrivileges;
    return { role: "AGENT", agentId, customPrivileges };
  }
  if (roles.includes("PROVIDER")) {
    const providerId = token.claims.providerId as string;
    return { role: "PROVIDER", providerId };
  }
  const customerId = (token.claims.customerId as string | null) ?? "";
  const customPrivileges = (token.claims.customPrivileges ?? {}) as CustomerCustomPrivileges;
  return { role: "CUSTOMER", customerId, customPrivileges };
}

class Auth {
  currentUser: User | undefined = undefined;

  async getToken(): Promise<string | undefined> {
    if (!this.currentUser) {
      return undefined;
    }
    return await this.currentUser.getIdToken();
  }

  onAuthStateChanged(callback: AuthCallback) {
    getAuth().onAuthStateChanged((user) => {
      this.currentUser = user ?? undefined;
      if (!user) {
        callback(null);
        setUserIdForAnalytics(null);
        setUserRoleForAnalytics(null);
        return;
      }
      setUserIdForAnalytics(user.email);
      user.getIdTokenResult().then((token) => {
        const context = contextFromToken(token);
        setUserRoleForAnalytics(context.role);
        callback({ info: user, context });
      });
    });
  }

  isSignInWithEmailLink(href: string) {
    return isSignInWithEmailLink(getAuth(), href);
  }

  signInWithEmailLink(email: string, href: string) {
    return signInWithEmailLink(getAuth(), email, href);
  }

  async signInWithGoogle() {
    const googleAuthProvider = new GoogleAuthProvider();
    if (isMobileBrowser()) {
      const firebaseAuth = getAuth();
      firebaseAuth.languageCode = "pl_PL";
      await signInWithRedirect(firebaseAuth, googleAuthProvider);
    } else {
      await signIn((firebaseAuth) => signInWithPopup(firebaseAuth, googleAuthProvider));
    }
  }

  async signInWithTravel360() {
    const travel360AuthProvider = new OAuthProvider("oidc.travel360");
    travel360AuthProvider.addScope("openid");
    if (isMobileBrowser()) {
      const firebaseAuth = getAuth();
      firebaseAuth.languageCode = "pl_PL";
      await signInWithRedirect(firebaseAuth, travel360AuthProvider);
    } else {
      await signIn((firebaseAuth) => signInWithPopup(firebaseAuth, travel360AuthProvider));
    }
  }

  async getRedirectResult() {
    return getRedirectResult(getAuth());
  }

  async signInWithEmail(email: string) {
    const actionCodeSettings = {
      url: window.location.href,
      handleCodeInApp: true,
    };
    await signIn((auth) => sendSignInLinkToEmail(auth, email, actionCodeSettings));
  }

  async signInWithEmailAndPassword(email: string, password: string) {
    await signIn((auth) => signInWithEmailAndPassword(auth, email, password));
  }

  async sendPasswordResetEmail(email: string) {
    await sendPasswordResetEmail(getAuth(), email);
  }

  async signUpWithEmailAndPassword(email: string, password: string) {
    await signUpWithEmailAndPassword(email, password);
  }

  signOut() {
    return getAuth().signOut();
  }
}

function isMobileBrowser(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

async function signUpWithEmailAndPassword(email: string, password: string) {
  try {
    const auth = getAuth();
    auth.languageCode = "pl_PL";
    await createUserWithEmailAndPassword(auth, email, password);
  } catch (error) {
    if (isErrorWithCode(error)) {
      if (isEmailAlreadyInUseError(error)) {
        console.warn(`Sign up failed because ${email} email is already in use. Details: ${error.message}`);
        throw new Error(
          `Użytkownik z przypisanym adresem ${email} jest istnieje. Przejdź do logowania a jeśli to nie ty go utworzyłeś, skontaktuj się z nami.`,
          { cause: error },
        );
      } else if (isInternalCloudFunctionError(error)) {
        const errorDetails = parseInternalCloudFunctionError(error);
        if (errorDetails) {
          const message = `Sing up failed because of internal Cloud Function error, status: ${errorDetails.status}. Details: ${errorDetails.message}`;
          if (errorDetails.message?.includes("deadline exceeded")) {
            console.error(message, error);
          } else {
            console.warn(message, error);
          }
          throw new Error(errorDetails.message, { cause: error });
        }
        console.warn(`Sign up failed because of internal Cloud Function error. Details: ${error.message}`);
        throw new Error(error.message, { cause: error });
      }
      console.warn(`Sign up failed, cause: ${error.code}. Details: ${error.message}`);
      throw new Error(error.message, { cause: error });
    } else {
      throw error;
    }
  }
}

async function signIn(execute: (auth: FirebaseAuth) => Promise<UserCredential | void>) {
  try {
    const auth = getAuth();
    auth.languageCode = "pl_PL";
    await execute(auth);
  } catch (error) {
    if (isErrorWithCode(error)) {
      // https://firebase.google.com/docs/auth/web/facebook-login#handling-account-exists-with-different-credential-errors
      if (isAccountExistError(error)) {
        const pendingCred = error.credential;
        const email = error.email ?? error.customData.email ?? "";
        const methods = await fetchSignInMethodsForEmail(getAuth(), email);
        const otherProvider = methods[0] === "google.com" ? new GoogleAuthProvider() : new EmailAuthProvider();
        const result = await signInWithPopup(getAuth(), otherProvider);
        await linkWithCredential(result.user, pendingCred);
      } else if (error.code === "auth/cancelled-popup-request") {
        console.warn(`Login failed because popup request has been cancelled. Details: ${error.message}`);
        throw new Error(
          "Przekierowanie na stronę logowania zostało zablokowane. Zezwól na otwieranie wyskakujących okienek w ustawieniach przeglądarki.",
          { cause: error },
        );
      } else if (error.code === "auth/popup-blocked") {
        console.warn(`Login failed because popup has been blocked. Details: ${error.message}`);
        throw new Error(
          "Przekierowanie na stronę logowania zostało zablokowane. Zezwól na otwieranie wyskakujących okienek w ustawieniach przeglądarki.",
          { cause: error },
        );
      } else if (isInternalCloudFunctionError(error)) {
        const errorDetails = parseInternalCloudFunctionError(error);
        if (errorDetails) {
          const message = `Login failed because of internal Cloud Function error, status: ${errorDetails.status}. Details: ${errorDetails.message}`;
          if (errorDetails.message?.includes("deadline exceeded")) {
            console.error(message, error);
          } else {
            console.warn(message, error);
          }
          throw new Error(errorDetails.message, { cause: error });
        }
        console.warn(`Login failed because of internal Cloud Function error. Details: ${error.message}`, error);
        throw new Error(error.message, { cause: error });
      } else {
        console.warn(`Login failed, cause: ${error.code}. Details: ${error.message}`, error);
        throw new Error(error.message, { cause: error });
      }
    } else {
      throw error;
    }
  }
}

function isInternalCloudFunctionError(error: unknown): error is FirebaseError {
  return (
    error instanceof Error &&
    "code" in error &&
    error.code === "auth/internal-error" &&
    error.message.includes("Cloud Function returned an error")
  );
}

export function isErrorWithCode(error: unknown): error is { code: string; message?: string } {
  return !!error && typeof error === "object" && "code" in error;
}

function isAccountExistError(error: unknown): error is {
  code: string;
  message?: string;
  credential: AuthCredential;
  email?: string;
  customData: { email?: string };
} {
  return isErrorWithCode(error) && error.code === "auth/account-exists-with-different-credential";
}

function isEmailAlreadyInUseError(error: unknown): error is {
  code: string;
  message?: string;
  credential: AuthCredential;
  email?: string;
  customData: { email?: string };
} {
  return isErrorWithCode(error) && error.code === "auth/email-already-in-use";
}

const internalCloudFunctionErrorPattern = /{\s*"error"\s*:\s*({.*})\s*}/;

function parseInternalCloudFunctionError(error: FirebaseError): { message: string; status: string } | null {
  const matchResult = error.message.match(internalCloudFunctionErrorPattern);
  if (matchResult && matchResult.length > 1) {
    return JSON.parse(matchResult[1]);
  }
  return null;
}

const env = import.meta.env.MODE;
export const auth: Auth = env === "test" ? mockAuth : new Auth();
